import { Directive, HostListener, ContentChild, AfterContentInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';

import { AutoComplete } from 'primeng/autocomplete';

import { HisBackendService } from '../../service/backend/his-backend.service';
import { HisAutoCompleteDefaults } from './his-autocomplete-defaults';

/**
 * Easy way to load a key table into an p-autoComplete component.
 * Also sets default values for p-autoComplete that are universally used in H1.
 */
@Directive( {
    selector: 'p-autoComplete[hisEagerDataSource]',
    exportAs: 'hisEagerDataSource',
    standalone: false
} )
export class HisAutoCompleteEagerDataSourceDirective
    extends HisAutoCompleteDefaults
    implements AfterContentInit, OnChanges {
    private backendService = inject(HisBackendService);
    private autoCompleteRef = inject(AutoComplete, { host: true });


    @Input( 'hisEagerDataSource' ) dataSource: string;

    @Output() hisDataSourceDataAvailable = new EventEmitter<any[]>();

    /** for eager access, store the complete list here */
    private allEntries: any[];

    ngAfterContentInit(): void {
        this.initDefaults( this.autoCompleteRef );

        // make name of data source available to debug panel
        // (needs some trickery to convince the compiler that AutoComplete has a field hisDataSource)
        (this.autoCompleteRef as any).hisDataSource = this.dataSource;
    }

    ngOnChanges( changes: SimpleChanges ): void {
        if ( changes.dataSource ) {
            this.loadAllValuesFromServer();
            if ( this.autoCompleteRef ) {
                // make name of data source available to debug panel
                (this.autoCompleteRef as any).hisDataSource = this.dataSource;
            }
        }
    }

    loadAllValuesFromServer() {
        let result$ = this.backendService.getData( this.expandBackendUrl( this.dataSource ) );
        result$.subscribe( (data: any[]) => {
            this.allEntries = data;
            // set suggestions here because hisDdKey is expecting it and @HostListener cannot read data of events
            this.autoCompleteRef.suggestions = data;
            // send an event to hisDdKey because direct access to autoCompleteRef.suggestions does not trigger change detection
            // (When @HostBinding supports custom @Input properties, that would be a much cleaner solution.
            this.hisDataSourceDataAvailable.emit( data );
        } );
    }

    filterEntriesOnClient( query: string ) {
        let dataSourceFilter = ( this.autoCompleteRef as any ).dataSourceFilter;
        let result: any[] = [];

        if ( dataSourceFilter ) {
            this.getResult( this.filterAllEntries( this.allEntries, dataSourceFilter ), query, result );
        } else {
            this.getResult( this.allEntries, query, result );
            // this.loadAllValuesFromServer();
        }
        this.autoCompleteRef.suggestions = result;
    }

    private getResult( allEntries: any[], query: string, result: any[] ): void {
        let queryInLowerCase = query.toLowerCase();
        let queryInLowerCaseArray = queryInLowerCase.split( ' ' );
        let prop = this.autoCompleteRef.optionLabel;

        if ( allEntries && allEntries.length > 0 ) {
            for (const entry of allEntries) {
                if (!entry) {
                    continue;
                }

                let value: string | undefined;

                if (typeof prop === 'function') {
                    value = prop(entry);
                } else if (typeof prop === 'string') {
                    value = entry[prop];
                } else {
                    value = entry.text;
                }

                if (typeof value !== 'string') {
                    continue;
                }

                if (queryInLowerCaseArray.every(item =>
                    value.toLowerCase().includes(item.toLowerCase())
                )) {
                    result.push(entry);
                }
            }
        }
    }

    /** host listener paste event implementation to set ngModel based on filtered suggestions */
    @HostListener( 'paste', ['$event'] )
    onExtendedPaste( event: ClipboardEvent ) {
        let pastedText = event.clipboardData.getData( 'Text' );
        // trigger set suggestions
        this.filterEntriesOnClient( pastedText );
        if ( this.autoCompleteRef.suggestions ) {
            let foundEntry = this.autoCompleteRef.suggestions.find( entry => entry.defaulttext === pastedText );
            if ( foundEntry ) {
                // set ngModel
                this.autoCompleteRef.onModelChange( foundEntry );
            }
        }
    }

    // filter the list of objects based on user input in the text field
    @HostListener( 'completeMethod', ['$event'] )
    filterValue( event: any ) {
        let query = event.query;
        this.filterEntriesOnClient( query );
    }

    /** filter allEntries withiout reload by dataSourceFilter parameter */
    public filterAllEntries( allEntries: any[], dataSourceFilter: any ): any[] {
        switch ( dataSourceFilter.split( '=' )[0].trim() ) {
            case 'hiskeyIds':
                return allEntries.filter( entry => dataSourceFilter.includes( ( entry.hiskeyId as string ) ) );
            case 'objGuids':
                return allEntries.filter( entry => dataSourceFilter.includes( ( entry.objGuid as string ) ) );
            default:
                console.log('#HisAutoCompleteDefaults.filterAllEntries() not valid datSourceFilter: ', dataSourceFilter );
                return allEntries;
        }
    }

}
